"use client"

import { useCallback, useMemo } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { useLocale } from "next-intl"
import { useCountry } from "@/components/layout/country-provider"
import type { NavbarItem } from "@/components/ui/navbar"
import type { FooterColumn } from "@/components/ui/footer"
import { bookingUrlForCountry, type BookingPackageType } from "@/lib/booking"
import { blobMediaUrl } from "@/lib/blob-url"
import { isAllowedInCountry, type GateId } from "@/lib/country-gates"
import { localizedPath } from "@/lib/localized-route-slugs"
import { isBookingOpen, isComingSoon, isWaitlistOnly } from "@/i18n/config"
import { useWaitlist } from "@/components/ui/waitlist-context"
import { baseBloodTest } from "@/lib/base-blood-test"
import { isServicePathHidden } from "@/lib/service-path-visibility"

/** Country-specific SEO landing pages exposed via the footer. Each entry
 *  pairs a footer translation key with the page's slug and the country-gate
 *  that governs the page — the link renders only where that page actually
 *  exists, so it can never point at a 404 (e.g. on NL/DE). */
type FooterLandingPage = {
  labelKey: string
  path: string
  gate: GateId
}

const FOOTER_LANDING_PAGES: readonly FooterLandingPage[] = [
  {
    labelKey: "fullBodyMriZurich",
    path: "/full-body-mri-zurich",
    // Zurich-specific SEO page — CH only (page:full-body-mri-zurich).
    gate: "page:full-body-mri-zurich",
  },
] as const

export function useLocalePath() {
  const locale = useLocale()
  const { code: country } = useCountry()
  return useCallback(
    (path: string) => {
      if (
        path.startsWith("http") ||
        path.startsWith("#") ||
        path.startsWith("tel:") ||
        path.startsWith("mailto:")
      )
        return path
      const bare = path.startsWith("/") ? path : `/${path}`
      return `/${country}/${locale}${localizedPath(bare, locale)}`
    },
    [locale, country]
  )
}

/** The `/{country}/{locale}` prefix for the current request, with no
 *  trailing slash. Pass to `sanitizeHtml()` to rewrite bare internal
 *  links in CMS-authored content. */
export function useLocalePrefix(): string {
  const locale = useLocale()
  const { code: country } = useCountry()
  return `/${country}/${locale}`
}

export function useLocalizedNavigation({
  membershipFirst = false,
  navHiddenPaths = [],
  footerBookingPackageType,
}: {
  /** Membership markets (CH + AT): the "How it works" mega-menu leads with an
   *  Ahead-membership card and the standalone Membership nav item is dropped on
   *  desktop. See `feature:membership-first-positioning` in lib/country-gates.ts. */
  membershipFirst?: boolean
  /** Bare `/tests/<slug>` paths hidden from the core nav for this country
   *  (CMS `products.marketStatus[].hideFromNav`). Filters the "What we offer"
   *  children; the hardcoded add-ons are filtered in the menu components. */
  navHiddenPaths?: string[]
  /** Booking-app tab the footer "book your check-up" link opens. Unset =
   *  no `packageType` (existing behaviour everywhere); `page-shell` passes the
   *  MRI tab on the MRI `/go` landing pages (AHD-7129). */
  footerBookingPackageType?: BookingPackageType
} = {}) {
  const t = useTranslations("nav")
  const ft = useTranslations("footer")
  const ut = useTranslations("ui")
  const locale = useLocale()
  const { code: country } = useCountry()
  const localePath = useLocalePath()
  const { openWaitlist } = useWaitlist()
  // Pre-launch markets (NL today) route the booking CTA to the waitlist modal
  // instead of the external booking flow — mirrors the navbar CTA in PageShell.
  const isWaitlist = isWaitlistOnly(country)

  // CH-only nav-item delta (shipped with the marketing-navbar-v3 launch):
  // Membership + Insurance primary slots, Pricing/Contact-us hidden on
  // desktop, "What we offer" → "Check-ups". NL/DE pre-launch markets keep
  // the simpler nav.
  const applyNavItemDelta = country === "ch"
  // NL nav delta: drop Pricing/FAQ/Contact (no reimbursement or support-desk
  // positioning pre-launch), add a Membership pill (no Insurance — not offered
  // there yet) and a Reviews link. Keeps the NL bar aligned with CH minus the
  // Swiss-only surfaces.
  const isNl = country === "nl"
  // NL + AT pre-launch nav delta: drop FAQ (no support-desk positioning
  // pre-launch) and add a Reviews link. The Membership pill stays NL-only — AT
  // follows DE's simpler nav. Pricing is no longer part of this delta: it
  // follows `page:pricing` directly, because Austria's waitlist site is a
  // marketing site and its whole job is to state the package prices.
  const isPreLaunchMarket = isNl || country === "at"
  // DE runs a lean nav/footer pre-MRI-launch (no imaging add-ons).
  const isDe = country === "de"
  // The market's base blood check-up: `prevention-starter` (Vorsorge-Starter)
  // in DE, `advanced-blood-test` everywhere else. Single source of truth so the
  // "What we offer" menu and the footer never hardcode the DE product.
  const baseBlood = baseBloodTest(country)

  const navItems = useMemo<NavbarItem[]>(
    () => [
      {
        // Trigger label stays "How it works"; in membership markets the dropdown
        // leads with an Ahead-membership card (and the standalone Membership nav
        // item is dropped — it lives here instead).
        label: t("howItWorks"),
        href: localePath("/how-it-works"),
        megaMenu: true,
        megaMenuType: "howItWorks" as const,
        children: [
          ...(membershipFirst
            ? [
                {
                  label: t("membership"),
                  description: t("mfMembershipMenuDesc"),
                  href: localePath("/membership"),
                  imageSrc: blobMediaUrl(
                    "/images/homepage-membership/menu-card.png"
                  ),
                },
              ]
            : []),
          {
            label: t("howItWorks"),
            description: t("howItWorksDesc"),
            href: localePath("/how-it-works"),
            imageSrc: blobMediaUrl("/images/mega-menu/how-it-works.jpg"),
          },
          {
            label: t("healthRisks"),
            description: t("healthRisksDesc"),
            href: localePath("/what-we-detect"),
            imageSrc: blobMediaUrl("/images/mega-menu/health-risks.jpg"),
          },
          {
            label: t("yourHealthReport"),
            description: t("healthReportDesc"),
            href: localePath("/report"),
            imageSrc: blobMediaUrl("/images/mega-menu/health-report.jpg"),
          },
          ...(isAllowedInCountry("page:biomarkers", country)
            ? [
                {
                  label: t("biomarkersWeCheck"),
                  description: t("biomarkersDesc"),
                  href: localePath("/biomarkers"),
                  imageSrc: blobMediaUrl("/images/mega-menu/biomarkers.jpg"),
                },
              ]
            : []),
        ],
      },
      {
        // CH renames the mega-menu trigger from "What we offer" →
        // "Check-ups" (and locale equivalents); NL/DE keep the original.
        label: applyNavItemDelta ? t("checkups") : t("whatWeOffer"),
        href: localePath("/tests"),
        megaMenu: true,
        megaMenuType: "whatWeOffer" as const,
        // Drop the featured services flagged hide-from-nav for this country
        // (CMS `hideFromNav`). The hardcoded add-ons are filtered in the menu
        // components against the same `navHiddenPaths`.
        children: [
          ...(navHiddenPaths.includes("/tests/full-body-mri")
            ? []
            : [
                {
                  label: t("fullBodyMri"),
                  href: localePath("/tests/full-body-mri"),
                },
              ]),
          ...(navHiddenPaths.includes(`/tests/${baseBlood.slug}`)
            ? []
            : [
                {
                  label: t(baseBlood.labelKey),
                  href: localePath(`/tests/${baseBlood.slug}`),
                },
              ]),
        ],
      },
      // Pricing — NL hides it pre-launch by choice (it has the page, but no
      // pricing positioning yet); everyone else shows it exactly when the page
      // exists. AT came back on 2026-09-28 once it had a EUR ladder to show,
      // and its homepage package cards deep-link here. CH keeps it but drops
      // it from the desktop bar (Membership + Insurance share the primary
      // slots); mobile slide-out still shows it.
      ...(isNl || !isAllowedInCountry("page:pricing", country)
        ? []
        : [
            {
              label: t("pricing"),
              href: localePath("/pricing"),
              ...(applyNavItemDelta ? { hideOnDesktop: true } : {}),
            },
          ]),
      // CH inserts Membership + Insurance between Pricing and Locations. NL
      // adds Membership only (Insurance isn't offered there yet). DE keeps the
      // simpler nav: it has no membership page at all (AHD-13290).
      // In membership markets the standalone Membership item is dropped on
      // desktop — membership leads the "How it works" mega-menu instead. It's a
      // plain conditional now that `membershipFirst` is country-derived (known
      // at render on both server and client, so there's nothing to flash). Kept
      // on mobile via the account block (the mega-menu isn't reachable there).
      ...(applyNavItemDelta
        ? [
            ...(membershipFirst
              ? []
              : [
                  {
                    label: t("membershipShort"),
                    href: localePath("/membership"),
                    // The mobile slide-out already links Membership from its
                    // account block — hide the nav item
                    // there so it doesn't appear twice (AHD-6728).
                    hideOnMobile: true,
                  },
                ]),
            { label: t("insurance"), href: localePath("/insurance") },
          ]
        : // Membership isn't offered in NL — no standalone Membership nav item
          // there (AHD-6910). Other pre-launch markets never had one.
          []),
      { label: t("locations"), href: localePath("/locations") },
      // FAQ — off the desktop bar on pre-launch markets (NL, AT), where the
      // Reviews slot below takes its place. This is a bar-layout choice, not a
      // hide: NL and AT both HAVE the page and link it from the footer.
      // Elsewhere it sits on the bar.
      ...(isPreLaunchMarket
        ? []
        : [
            {
              label: t("faq"),
              href: localePath("/faq"),
            },
          ]),
      // Reviews — pre-launch markets (NL, AT) only. Kept out of the CH primary
      // nav: the navbar-v4 test showed the Reviews slot drew about half the
      // traffic of the FAQ slot it would have replaced, and the bundle hurt
      // desktop conversion.
      ...(isPreLaunchMarket
        ? [{ label: t("reviews"), href: localePath("/reviews") }]
        : []),
      // Contact us — hidden on NL during its waitlist phase. AT keeps it: the
      // trimmed Austrian launch set (2026-08-28) includes /contact, so the nav
      // has to reach it. CH drops it from the desktop bar (room for Insurance)
      // but keeps it in the mobile slide-out.
      ...(isNl
        ? []
        : [
            {
              label: ut("contactUs"),
              href: localePath("/contact"),
              ...(applyNavItemDelta ? { hideOnDesktop: true } : {}),
            },
          ]),
    ],
    [
      t,
      ut,
      localePath,
      applyNavItemDelta,
      isPreLaunchMarket,
      isNl,
      country,
      membershipFirst,
      navHiddenPaths,
      baseBlood,
    ]
  )

  const footerColumns = useMemo<FooterColumn[]>(() => {
    // DE launches as a lean footer: the Ganzkörper-MRT, the imaging add-ons,
    // membership, careers, ambassador programme and several info pages aren't
    // live there yet.
    const columns: FooterColumn[] = [
      {
        title: ft("information"),
        links: [
          { label: ft("howItWorks"), href: localePath("/how-it-works") },
          { label: ft("healthRisks"), href: localePath("/what-we-detect") },
          ...(isAllowedInCountry("page:biomarkers", country)
            ? [{ label: ft("biomarkers"), href: localePath("/biomarkers") }]
            : []),
          ...(isAllowedInCountry("page:pricing", country)
            ? [{ label: ft("pricing"), href: localePath("/pricing") }]
            : []),
          // DE pre-launch: trim the footer to the live surfaces — Wissen
          // (insights), Kostenübernahme and "Seite durchsuchen" are
          // hidden until those pages are ready for the German market. Every
          // other market follows the page gate, so AT picked both back up on
          // 2026-09-28 and fills them from the CMS one document at a time.
          ...(!isDe && isAllowedInCountry("page:insights", country)
            ? [{ label: ft("insights"), href: localePath("/insights") }]
            : []),
          ...(isAllowedInCountry("page:faq", country)
            ? [{ label: ft("faq"), href: localePath("/faq") }]
            : []),
          // Reimbursement link: CH → the /insurance checker. DE hides its
          // Kostenübernahme page pre-launch; NL has no positioning yet.
          ...(!isDe && isAllowedInCountry("page:insurance", country)
            ? [
                {
                  label: ft("insuranceCoverage"),
                  href: localePath("/insurance"),
                },
              ]
            : []),
          // CH-only: the comparison page is Switzerland-specific (AHD-6411).
          ...(country === "ch"
            ? [
                {
                  label: ft("howAheadCompares"),
                  href: localePath("/compare-health-check-ups-switzerland"),
                },
              ]
            : []),
          ...(!isDe
            ? [{ label: ft("searchSite"), href: localePath("/search") }]
            : []),
          // Last item of the column on purpose: the free calculators are a
          // top-of-funnel surface, not a product page, so they sit below the
          // pages someone comparing check-ups actually needs. The gate is
          // already CH + DE + NL (AT excluded, no tools copy in de-AT), so
          // this renders in every market that has a /tools index.
          ...(isAllowedInCountry("page:tools", country)
            ? [
                {
                  label: ft("healthCalculators"),
                  href: localePath("/tools"),
                },
              ]
            : []),
        ],
      },
      {
        title: ft("bookingCheckup"),
        links: [
          // Pre-launch markets (NL) have no booking flow yet — the link opens
          // the waitlist modal AND reads "Join waitlist" (2026-07-29), same
          // label as the navbar CTA.
          isWaitlist
            ? { label: ut("joinWaitlist"), onClick: () => openWaitlist() }
            : {
                label: ft("bookYourCheckup"),
                href: bookingUrlForCountry(
                  country,
                  locale,
                  undefined,
                  undefined,
                  footerBookingPackageType
                ),
              },
          // Discovery-call page is gated out of NL during the waitlist phase.
          ...(isAllowedInCountry("page:free-call", country)
            ? [
                {
                  label: ft("scheduleDiscoveryCall"),
                  href: localePath("/free-call"),
                },
              ]
            : []),
          { label: ft("findNearestClinic"), href: localePath("/locations") },
          ...FOOTER_LANDING_PAGES.filter((page) =>
            isAllowedInCountry(page.gate, country)
          ).map(({ labelKey, path }) => ({
            label: ft(labelKey as Parameters<typeof ft>[0]),
            href: localePath(path),
          })),
        ],
      },
      {
        title: ft("whatWeTest"),
        // Every entry is a `/tests/<slug>` link, so the list is written as
        // bare paths and filtered through the same predicate the mega-menu,
        // the mobile menu and the homepage carousel use. It was the one
        // hardcoded service list that never consulted it, and on /at/de five
        // of its six links pointed at routes that 404 — Austria marks every
        // standalone test `at-de: false`, which no `page:` gate expresses.
        //
        // DE is still a flag rather than a path check: it sells the blood
        // check-up only pre-launch, so it drops the MRI link and the imaging
        // add-ons as a product decision, not because those routes are missing.
        links: [
          ...(isDe
            ? []
            : [
                {
                  label: ft("fullBodyMri"),
                  path: "/tests/full-body-mri",
                  ...(isComingSoon(country, "mri")
                    ? { badge: ut("comingSoon") }
                    : {}),
                },
              ]),
          { label: t(baseBlood.labelKey), path: `/tests/${baseBlood.slug}` },
          ...(isDe
            ? []
            : [
                { label: ft("calciumScore"), path: "/tests/calcium-score" },
                // Gender-health is intentionally NOT surfaced here:
                // prostate-mri + pelvic-mri replaced it, so the combined page
                // stays routable but hidden from nav (TEST_LISTING_HIDDEN_SLUGS).
                { label: ft("dexaScan"), path: "/tests/dexa-scan" },
                {
                  label: ft("bodyComposition"),
                  path: "/tests/body-composition",
                },
                { label: ft("brainAnalysis"), path: "/tests/brain-volumetry" },
              ]),
          // Health-concern organic pages. Each is gated so the link renders
          // only where the page exists (CH for now) — never a 404. Points at
          // the clean organic URL, not the /go ad LP.
          ...(isAllowedInCountry("page:heart-health", country)
            ? [{ label: ft("heartHealth"), path: "/heart-health" }]
            : []),
          ...(isAllowedInCountry("page:metabolism", country)
            ? [{ label: ft("metabolism"), path: "/metabolism" }]
            : []),
          ...(isAllowedInCountry("page:female-hormones", country)
            ? [{ label: ft("femaleHormones"), path: "/female-hormones" }]
            : []),
        ]
          .filter(
            ({ path }) =>
              !isServicePathHidden({ path, country, locale, navHiddenPaths })
          )
          .map(({ path, ...link }) => ({ ...link, href: localePath(path) })),
      },
      {
        title: ft("company"),
        links: [
          { label: ft("aboutUs"), href: localePath("/about") },
          // Careers + ambassador programme are hidden on DE pre-launch, and
          // don't exist at all on AT (page:careers / page:ambassadors).
          ...(!isDe && isAllowedInCountry("page:careers", country)
            ? [
                {
                  label: ft("careers"),
                  href: localePath("/careers"),
                  badge: ft("hiringBadge"),
                },
              ]
            : []),
          { label: ft("contactUs"), href: localePath("/contact") },
          ...(!isDe && isAllowedInCountry("page:ambassadors", country)
            ? [
                {
                  label: ft("ambassadorProgram"),
                  href: localePath("/ambassadors"),
                },
              ]
            : []),
          { label: ft("reviews"), href: localePath("/reviews") },
        ],
      },
    ]

    // Ahead Membership in the footer only where its booking flow is open (CH).
    // DE (no page, AHD-13290) and waitlist NL (not offered) both stay out — a footer
    // link is a plain promise with no pill to qualify it.
    if (isBookingOpen(country, "membership")) {
      const booking = columns.find((c) => c.title === ft("bookingCheckup"))
      booking?.links.unshift({
        label: t("membership"),
        href: localePath("/membership"),
      })
    }

    return columns
  }, [
    ft,
    t,
    ut,
    localePath,
    locale,
    country,
    isDe,
    isWaitlist,
    openWaitlist,
    baseBlood,
    footerBookingPackageType,
  ])

  const footerLabels = useMemo(
    () => ({
      tagline: ft("tagline"),
      whatsapp: ft("whatsapp"),
      copyright: ft("copyright"),
      legalLinks: [
        // AT still hides T&C: that page 404s there until Austrian counsel
        // delivers (AHD-6893), so linking it would be a dead link. Privacy is
        // linked as of 2026-08-28 — the route renders, and a market that
        // collects waitlist emails has to be able to reach a
        // Datenschutzerklärung. NL's own documents went live in AHD-7917.
        //
        // The link is NOT compliance. Every section body in the de-AT
        // `privacy` namespace is still a `[TODO: legal]` placeholder behind a
        // visible "wird derzeit finalisiert" banner — exactly the stub
        // /imprint already ships on AT. Real content has to land before
        // AT_STATUS leaves `built` and the market goes public.
        ...(country === "at"
          ? []
          : [
              {
                label: ft("termsConditions"),
                href: localePath("/terms-conditions"),
              },
            ]),
        { label: ft("privacyPolicy"), href: localePath("/privacy") },
        { label: ft("imprint"), href: localePath("/imprint") },
      ],
      cookieSettings: ft("cookieSettings"),
    }),
    [ft, localePath, country]
  )

  return { navItems, footerColumns, footerLabels }
}
